import React from 'react';

interface InputFileProps {
  name: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileName?: string | string[];
  style?: React.CSSProperties;
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  label?: string | React.ReactNode;
  wrapperClass?: string;
  labelClassName?: string;
  accept?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  refInput?: React.Ref<HTMLInputElement>;
  multiple?: boolean;
  disabled?: boolean;
}

export const InputFile: React.FC<InputFileProps> = ({
  fileName,
  name,
  error,
  label,
  wrapperClass,
  labelClassName,
  accept,
  onChange,
  refInput,
  disabled = false,
  multiple = false,
}: InputFileProps) => {
  const formattedFileNames =
    typeof fileName === 'string'
      ? fileName
      : (fileName?.join(', ') ?? 'Nenhum arquivo selecionado');

  return (
    <div className={`space-y-2 ${wrapperClass}`}>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium text-gray-700 ${labelClassName}`}
        >
          {label}
        </label>
      )}

      <div
        className={`
        relative w-full
        bg-white border-2 border-dashed 
        ${error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-red-400'}
        rounded-lg p-4
        transition-all duration-300 ease-in-out
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
        group min-h-[80px]
      `}
      >
        <input
          type="file"
          accept={accept}
          name={name}
          id={name}
          onChange={onChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          ref={refInput}
          multiple={multiple}
          disabled={disabled}
        />

        <div className="text-center">
          <div
            className={`
            mx-auto w-8 h-8 mb-2
            ${error ? 'text-red-400' : 'text-gray-400 group-hover:text-red-500'}
            transition-colors duration-300
          `}
          >
            <svg
              className="w-full h-full"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>

          <div className="space-y-1">
            {fileName && fileName !== 'Nenhum arquivo selecionado' ? (
              <p className="text-sm font-medium text-red-600 truncate px-2">
                {formattedFileNames}
              </p>
            ) : (
              <>
                <p
                  className={`text-sm font-medium ${error ? 'text-red-600' : 'text-gray-900'}`}
                >
                  <span
                    className={`${error ? 'text-red-600' : 'text-red-600 hover:text-red-700'} transition-colors`}
                  >
                    Escolher arquivo{multiple ? 's' : ''}
                  </span>
                  <span className="text-gray-500"> ou arraste aqui</span>
                </p>
              </>
            )}

            <p className="text-xs text-gray-500">
              {multiple ? 'PNG, JPG até 2MB cada' : 'PNG, JPG até 2MB'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span role="alert">{error}</span>
        </div>
      )}
    </div>
  );
};
